/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AccountAvailabilityResponse } from "../models/AccountAvailabilityResponse";
import type { AccountAvailabilityUpdate } from "../models/AccountAvailabilityUpdate";
import type { AvailabilityMessage } from "../models/AvailabilityMessage";
import type { AvailableTutorsMessage } from "../models/AvailableTutorsMessage";
import type { SessionFrequency } from "../models/SessionFrequency";
import type { TutorSeriesAvailabilityResponse } from "../models/TutorSeriesAvailabilityResponse";

import type { CancelablePromise } from "../core/CancelablePromise";
import { OpenAPI } from "../core/OpenAPI";
import { request as __request } from "../core/request";

export class AvailabilitiesService {
  /**
   * Get Availabilities By Account
   * Get the base "hours" an account has selected they are available for tutoring.
   * @returns AccountAvailabilityResponse Successful Response
   * @throws ApiError
   */
  public static getAvailabilitiesByAccount(): CancelablePromise<AccountAvailabilityResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/availabilities",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Update Account Availabilities
   * Update an account's daily availabilities by their id using specified attributes
   * within AccountAvailabilityUpdate to update the database and return the account's updated
   * daily availabilities.
   * @returns AccountAvailabilityResponse Successful Response
   * @throws ApiError
   */
  public static updateAccountAvailabilities({
    requestBody,
  }: {
    requestBody: AccountAvailabilityUpdate;
  }): CancelablePromise<AccountAvailabilityResponse> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/availabilities",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Availabilities By Tutor
   * Between `start` and `until`, get the times a tutor's account has indicated they are
   * available for tutoring, excluding existing meetings that tutor's account has (across
   * all students and tutors linked to the account) and google calendar events.
   * @returns AvailabilityMessage Successful Response
   * @throws ApiError
   */
  public static getAvailabilitiesByTutor({
    tutorId,
    until,
    start,
    duration = 60,
  }: {
    tutorId: number;
    until: string;
    start: string;
    duration?: number;
  }): CancelablePromise<Array<AvailabilityMessage>> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/availabilities/tutor",
      query: {
        tutor_id: tutorId,
        until: until,
        start: start,
        duration: duration,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Availabilities By Tutor For Public Booking Page
   * A listed tutor's open times between `start` and `until` for a public
   * booking page: the org's length rule, minimum notice, booking window and
   * buffer apply, and requests waiting for the tutor hold their time.
   * @returns AvailabilityMessage Successful Response
   * @throws ApiError
   */
  public static getAvailabilitiesByTutorForPublicBookingPage({
    tutorId,
    until,
    start,
    duration,
  }: {
    tutorId: number;
    until: string;
    start: string;
    duration?: number | null;
  }): CancelablePromise<Array<AvailabilityMessage>> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/availabilities/tutor/public_booking_page",
      query: {
        tutor_id: tutorId,
        until: until,
        start: start,
        duration: duration,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Tutors By Availability
   * For all tutors in an organization, categorize them based on their availability:
   * - exact_availability: Tutors available at `exact_start` for `duration`.
   * - general_availability: Tutors available at some time between `start` and `until`
   * for `duration`, but not at `exact_start`.
   *
   * All availabilities are filtered to exclude events/meetings.
   * @returns AvailableTutorsMessage Successful Response
   * @throws ApiError
   */
  public static getTutorsByAvailability({
    orgId,
    start,
    until,
    exactStart,
    duration,
  }: {
    orgId: number;
    start: string;
    until: string;
    exactStart: string;
    duration: number;
  }): CancelablePromise<AvailableTutorsMessage> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/availabilities/tutors_by_availability",
      query: {
        org_id: orgId,
        start: start,
        until: until,
        exact_start: exactStart,
        duration: duration,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Tutors Series Availability
   * The selected tutors on every date of a new series (stepped in the org's
   * timezone, as booking does), busy as the public booking page counts it:
   * outside their weekly hours, or during their accepted and pending meetings
   * in every org of their accounts or their Google busy times. busy_dates
   * gives each busy date the nearest free start that day, free for every
   * tutor and the location (if any); weekly_starts gives up to two first
   * starts whose clock time is free on every date.
   * @returns TutorSeriesAvailabilityResponse Successful Response
   * @throws ApiError
   */
  public static getTutorsSeriesAvailability({
    orgId,
    tutorIds,
    start,
    duration,
    frequency,
    endsOnOrBefore,
    locationId,
  }: {
    orgId: number;
    tutorIds: Array<number>;
    start: string;
    duration: number;
    frequency?: SessionFrequency | null;
    endsOnOrBefore?: string | null;
    locationId?: number | null;
  }): CancelablePromise<TutorSeriesAvailabilityResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/availabilities/organizations/{org_id}/series",
      path: {
        org_id: orgId,
      },
      query: {
        tutor_ids: tutorIds,
        start: start,
        duration: duration,
        frequency: frequency,
        ends_on_or_before: endsOnOrBefore,
        location_id: locationId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Tutor Availabilities As Admin
   * An admin reads a tutor's weekly availability, the same hours the tutor sets.
   * @returns AccountAvailabilityResponse Successful Response
   * @throws ApiError
   */
  public static getTutorAvailabilitiesAsAdmin({
    tutorId,
  }: {
    tutorId: number;
  }): CancelablePromise<AccountAvailabilityResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/availabilities/tutors/{tutor_id}",
      path: {
        tutor_id: tutorId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Update Tutor Availabilities As Admin
   * An admin changes a tutor's weekly availability, as the tutor would.
   * @returns AccountAvailabilityResponse Successful Response
   * @throws ApiError
   */
  public static updateTutorAvailabilitiesAsAdmin({
    tutorId,
    requestBody,
  }: {
    tutorId: number;
    requestBody: AccountAvailabilityUpdate;
  }): CancelablePromise<AccountAvailabilityResponse> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/availabilities/tutors/{tutor_id}",
      path: {
        tutor_id: tutorId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }
}
